import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { backendService } from "../../services/backend.service";
import type { DisciplinaryReportResponse, DisciplinaryRulesResponse, DisciplinaryViolationType, NurseSortItem } from "../../utils/models";

const disciplinaryReportKey = "disciplinary-report";
const disciplinaryRulesKey = "disciplinary-rules";

export const useGetDisciplinaryReport = ({
	startDate,
	endDate,
	roles,
	employmentTypes,
	shiftTypes,
	violationTypes,
	shiftHours,
	departmentId: departmentIdOverride,
}: {
	startDate?: DateTime;
	endDate?: DateTime;
	roles?: string[];
	employmentTypes?: string[];
	shiftTypes?: string[];
	violationTypes?: DisciplinaryViolationType[];
	shiftHours?: string[];
	departmentId?: string;
} = {}) => {
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	const deptId = departmentIdOverride || chosenDepartment?.department_id;

	return useQuery({
		queryKey: [disciplinaryReportKey, deptId, startDate?.toISO(), endDate?.toISO(), roles, employmentTypes, shiftTypes, violationTypes, shiftHours],
		queryFn: async (): Promise<DisciplinaryReportResponse | null> => {
			if (!deptId) {
				return null;
			}
			try {
				const response = await backendService.getDisciplinaryReport({
					departmentId: deptId,
					startDate,
					endDate,
					roles,
					employmentTypes,
					shiftTypes,
					shiftHours,
					violationTypes,
				});
				return response.data;
			} catch (error) {
				showErrorSnackbar("Failed to fetch disciplinary report.", {}, error);
				return null;
			}
		},
		staleTime: 60 * 1000,
		throwOnError: false,
		enabled: !!deptId,
	});
};

export const useGetDisciplinaryRules = () => {
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();

	return useQuery({
		queryKey: [disciplinaryRulesKey, chosenDepartment?.department_id],
		queryFn: async (): Promise<DisciplinaryRulesResponse | null> => {
			if (!chosenDepartment?.department_id) return null;
			try {
				const response = await backendService.getDisciplinaryRules(chosenDepartment.department_id);
				return response.data;
			} catch (error) {
				showErrorSnackbar("Failed to fetch disciplinary rules.", {}, error);
				return null;
			}
		},
		throwOnError: false,
		enabled: !!chosenDepartment?.department_id,
	});
};

export const useExportDisciplinaryReport = () => {
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	const { track } = useAnalytics();

	return useMutation({
		mutationFn: async ({
			exportFormat,
			dateSortOrder,
			nurseSort,
			startDate,
			endDate,
			roles,
			employmentTypes,
			shiftTypes,
			shiftHours,
			violationTypes,
		}: {
			exportFormat: "pretty" | "raw" | "overview";
			dateSortOrder: "asc" | "desc";
			nurseSort?: NurseSortItem[];
			startDate?: DateTime;
			endDate?: DateTime;
			roles?: string[];
			employmentTypes?: string[];
			shiftTypes?: string[];
			shiftHours?: string[];
			violationTypes?: DisciplinaryViolationType[];
		}): Promise<ArrayBuffer> => {
			const body = {
				departmentId: chosenDepartment.department_id,
				exportFormat,
				dateSortOrder,
				nurseSort,
				startDate,
				endDate,
				roles,
				employmentTypes,
				shiftTypes,
				shiftHours,
				violationTypes,
			};
			track("disciplinary_report_export_start", { body: JSON.stringify(body) });
			const response = await backendService.exportDisciplinaryReport(body);
			track("disciplinary_report_exported", { body: JSON.stringify(body) });
			return response.data;
		},
		onError: (error) => {
			showErrorSnackbar("Failed to export disciplinary report.", {}, error);
		},
		throwOnError: false,
	});
};

export const useSaveDisciplinaryNote = () => {
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();
	const { track } = useAnalytics();

	return useMutation({
		mutationFn: async ({
			shiftId,
			shiftActualId,
			violationType,
			notes,
		}: {
			shiftId: string;
			shiftActualId: string | null;
			violationType: DisciplinaryViolationType;
			notes: string | null;
		}) => {
			track("disciplinary_note_save", { shift_id: shiftId, violation_type: violationType });
			await backendService.saveDisciplinaryNote({
				departmentId: chosenDepartment.department_id,
				shiftId,
				shiftActualId,
				violationType,
				notes,
			});
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: [disciplinaryReportKey] }),
				// Keep the nurse profile "Last Call-In's" Message column in sync for callin/NCNS notes.
				queryClient.invalidateQueries({ queryKey: ["limitedCallOff"] }),
			]);
		},
		onError: (error) => {
			showErrorSnackbar("Failed to save note.", {}, error);
		},
		throwOnError: false,
	});
};

export const useSaveDisciplinaryRules = () => {
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async (rules: DisciplinaryRulesResponse) => {
			await backendService.saveDisciplinaryRules(chosenDepartment.department_id, rules);
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: [disciplinaryRulesKey] });
			await queryClient.invalidateQueries({ queryKey: [disciplinaryReportKey] });
			showSnackbar(t("disciplinary.settings.saved"), "success");
		},
		onError: (error) => {
			showErrorSnackbar("Failed to save disciplinary rules", {}, error);
		},
		throwOnError: false,
	});
};
